import { useMemo, useState, useCallback } from "react";
import { ICrewRuleCategory, ICrewRuleCategoryAssignment } from "@vmosolutions/oc-interfaces";
import { Formik, FormikProps, FormikHelpers } from "formik";
import { FormikSelectField, FormikDateTimeField } from "../../../MUI5FormikComponents/FormikWrappers";
import { Grid, Button, Modal, Paper, ButtonGroup, Box } from "@mui/material";
import { IActiveModal } from "src/pages/Admin/AdminWindow";
import { CrewFormType } from "./CrewForm";
import cloneDeep from "lodash/cloneDeep";
import * as Yup from "yup";
import { useSelector } from "react-redux";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import moment from "moment-timezone";
import { DraggableListItem } from "src/components/DraggableListItem/DraggableListItem";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";

interface ICrewRuleCategoryProps {
  crewForm: CrewFormType;
  setActiveModal: (activeModal: IActiveModal | null) => void;
  showRuleCategoryModal: boolean;
  setShowRuleCategoryModal: any;
}

const initialCrewRuleCategoryValues: ICrewRuleCategoryAssignment = {
  _id: undefined,
  ruleCategory: {
    _id: undefined,
    name: "",
    appliesTo: [],
  },
  start: undefined,
  end: undefined,
};

type CrewRuleCategoryForm = FormikProps<ICrewRuleCategoryAssignment>;
export const CrewRuleCategoryField = (props: ICrewRuleCategoryProps): JSX.Element => {
  const { crewForm, showRuleCategoryModal, setActiveModal, setShowRuleCategoryModal } = props;
  const crewRuleCategories = useSelector((state: IRootState) => state.structures.customerStructure.crewRuleCategories);
  const showRuleEditor = crewRuleCategories?.length > 0;
  const assignedCRCs = useMemo(
    () => crewForm?.values?.crewInfo?.ruleCategoryAssignments ?? [],
    [crewForm?.values?.crewInfo?.ruleCategoryAssignments]
  );
  // Rows are tracked by position, not _id: saved rule category assignments have no _id, so matching on it
  // always hit the first row. null means a new row is being added.
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const selectedCRC = (selectedIndex !== null && assignedCRCs[selectedIndex]) || initialCrewRuleCategoryValues;
  const CrewRuleCategorySchema = Yup.object().shape({
    ruleCategory: Yup.object().nullable().required("Rule Category Required"),
    start: Yup.date().nullable(),
    end: Yup.date().nullable(),
  });
  const handleSubmit = useCallback(
    (values: ICrewRuleCategoryAssignment, helpers: FormikHelpers<ICrewRuleCategoryAssignment>): void => {
      const { setStatus, resetForm } = helpers;
      const newValues = cloneDeep(assignedCRCs);
      if (selectedIndex !== null && selectedIndex < newValues.length) {
        newValues[selectedIndex] = values;
      } else {
        newValues.push(values);
      }
      crewForm.setFieldValue("crewInfo[ruleCategoryAssignments]", newValues, true);
      crewForm.setFieldTouched("crewInfo[ruleCategoryAssignments]", true, false);
      setStatus({ success: true });
      setSelectedIndex(null);
      resetForm();
    },
    [assignedCRCs, crewForm, selectedIndex]
  );

  const handleDeleteSelected = useCallback(
    (deletedIndex: number): void => {
      setActiveModal({
        title: "Delete Rule Category",
        message: "Please confirm that you want to delete this rule category",
        cancel: () => setActiveModal(null),
        confirm: (): Promise<void> => {
          if (deletedIndex < assignedCRCs.length) {
            const updatedAssigned = [...assignedCRCs];
            updatedAssigned.splice(deletedIndex, 1);
            crewForm.setFieldValue("crewInfo[ruleCategoryAssignments]", updatedAssigned, true);
            crewForm.setFieldTouched("crewInfo[ruleCategoryAssignments]", true, false);
            setSelectedIndex(null);
          }
          setActiveModal(null);
          return Promise.resolve();
        },
        confirmLabel: "Confirm",
      });
    },
    [assignedCRCs, crewForm, setActiveModal]
  );

  return (
    <Modal open={showRuleCategoryModal}>
      <Paper elevation={4} sx={{ width: "65%", margin: "auto", marginTop: "10%", p: 4 }}>
        <Grid container item xs={12}>
          <Grid item xs={8}>
            {assignedCRCs?.length > 0 &&
              assignedCRCs.map((CRA: ICrewRuleCategoryAssignment, index: number) => {
                return (
                  <DraggableListItem
                    active={index === selectedIndex}
                    key={index}
                    sx={{ alignItems: "center", display: "flex", cursor: "pointer", justifyContent: "space-between" }}
                    onClick={() => setSelectedIndex(index)}
                  >
                    <Box>{CRA.ruleCategory.name}</Box>
                    <Box>
                      {moment(CRA.start).tz("UTC").format("MMM D, YYYY")}
                      {CRA?.end && (
                        <>
                          {" - "}
                          {moment(CRA.end).tz("UTC").format("MMM DD, YYYY")}
                        </>
                      )}
                    </Box>
                    <Box
                      sx={{
                        cursor: "pointer",
                        alignItems: "center",
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <EditIcon
                        fontSize="small"
                        sx={{ mr: 1 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedIndex(index);
                        }}
                      />
                      <DeleteIcon
                        fontSize="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteSelected(index);
                        }}
                      />
                    </Box>
                  </DraggableListItem>
                );
              })}
            <Button
              color="success"
              variant="contained"
              disableRipple
              fullWidth
              onClick={() => setSelectedIndex(null)}
            >
              <AddIcon fontSize="small" />
              Add Rule Category
            </Button>
          </Grid>
          {showRuleEditor && (
            <Grid item xs={4}>
              <Formik
                initialValues={selectedCRC}
                enableReinitialize={true}
                onSubmit={handleSubmit}
                validationSchema={CrewRuleCategorySchema}
              >
                {(subForm: CrewRuleCategoryForm) => {
                  const { values, dirty, isValid, isSubmitting, handleReset } = subForm;

                  return (
                    <>
                      <Grid item xs={12}>
                        <FormikSelectField
                          className="field selectfield"
                          name="ruleCategory"
                          label="Rule Category"
                          options={crewRuleCategories}
                          getOptionLabel={(option: ICrewRuleCategory) => option?.name ?? ""}
                          getOptionValue={(option: ICrewRuleCategory) => option?._id ?? ""}
                          placeholder="Select Rule Category..."
                          isClearable={false}
                          hideSelectedOptions={false}
                        />
                        <FormikDateTimeField label="Assigned Start Date" name="start" hideTimeControl={true} />

                        <FormikDateTimeField label="Assigned End Date" name="end" hideTimeControl={true} />
                      </Grid>
                      <Grid item xs={12} sx={{ display: "flex", justifyContent: "end", p: 2 }}>
                        <ButtonGroup variant="contained" size="small">
                          <Button
                            type="reset"
                            color="warning"
                            disabled={(!dirty && isValid) || isSubmitting}
                            onClick={handleReset}
                          >
                            Reset
                          </Button>
                          <Button
                            color="success"
                            disabled={isSubmitting || !isValid || !dirty}
                            onClick={() => {
                              handleSubmit(values, subForm);
                            }}
                          >
                            Save
                          </Button>
                        </ButtonGroup>
                      </Grid>
                    </>
                  );
                }}
              </Formik>
            </Grid>
          )}
          <Grid item xs={12} sx={{ display: "flex", justifyContent: "end", pt: 2, borderTop: "1px solid grey" }}>
            <Button
              size="small"
              onClick={() => {
                setSelectedIndex(null);
                setShowRuleCategoryModal(false);
              }}
              variant="contained"
              color="error"
            >
              Close
            </Button>
          </Grid>
        </Grid>
      </Paper>
    </Modal>
  );
};
